import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertCircle,
  Coins,
  Edit2,
  FileText,
  Loader2,
  Pause,
  Play,
  Plus,
  RotateCw,
  Send,
  Trash2,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../../components/ui/Modal'
import RatePickerModal from '../../components/rates/RatePickerModal'
import { formatCents, parseEurosToCents, centsToEurosInput } from '../../lib/money'
import {
  RecurringInvoicePeriodicityLabel,
  RecurringInvoiceStatusLabel,
  type ClientListResponse,
  type CreateRecurringInvoiceInput,
  type InvoiceSeriesDto,
  type RatePickerItem,
  type RecurringInvoiceDto,
  type RecurringInvoiceLineInput,
  type RecurringInvoiceListResponse,
  type RecurringInvoicePeriodicityT,
  type RecurringInvoiceStatusT,
} from '@toga/shared'

const STATUS_STYLES: Record<RecurringInvoiceStatusT, string> = {
  ACTIVE: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  PAUSED: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  CANCELED: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
}

export default function RecurringSettingsPage() {
  const [items, setItems] = useState<RecurringInvoiceDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<RecurringInvoiceDto | 'new' | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<RecurringInvoiceListResponse>('/recurring-invoices')
      setItems(resp.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const action = async (id: string, op: 'pause' | 'resume' | 'cancel' | 'generate-now') => {
    try {
      const r = await api.post<{ invoiceId?: string }>(`/recurring-invoices/${id}/${op}`, {})
      if (op === 'generate-now' && r?.invoiceId) {
        if (confirm(`Borrador creado. ¿Abrirlo ahora?`)) {
          window.location.href = `/billing/${r.invoiceId}`
          return
        }
      }
      await load()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      alert(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    }
  }

  const onDelete = async (r: RecurringInvoiceDto) => {
    if (!confirm(`¿Eliminar la iguala "${r.name}"?`)) return
    try {
      await api.delete(`/recurring-invoices/${r.id}`)
      await load()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">Igualas mensuales</h2>
          <p className="t-muted text-sm mt-1">
            Suscripciones de facturación recurrente. Cada periodo se genera un{' '}
            <strong>borrador</strong> en /billing para que lo revises y emitas a mano.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
        >
          <Plus size={14} /> Nueva iguala
        </button>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/30 text-xs uppercase tracking-wider t-faint">
            <tr>
              <th className="px-3 py-2 text-left">Nombre</th>
              <th className="px-3 py-2 text-left">Cliente</th>
              <th className="px-3 py-2 text-left">Periodicidad</th>
              <th className="px-3 py-2 text-left">Próximo</th>
              <th className="px-3 py-2 text-right">Total/periodo</th>
              <th className="px-3 py-2 text-center">Generadas</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin" /> Cargando…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-10 text-center">
                  <RotateCw size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">
                    Sin igualas configuradas. Crea la primera para facturar mensualmente
                    sin acordarte cada mes.
                  </p>
                </td>
              </tr>
            )}
            {items.map((r) => {
              const total = r.lines.reduce(
                (acc, l) => acc + Math.round(Number(l.quantity) * l.unitPriceCents),
                0
              )
              return (
                <tr key={r.id} className="border-t border-base/40 hover:bg-hover">
                  <td className="px-3 py-2 t-primary font-medium">{r.name}</td>
                  <td className="px-3 py-2 t-secondary">
                    <Link to="/clients" className="hover:underline">
                      {r.clientDisplayName}
                    </Link>
                    {r.caseCode && (
                      <span className="text-[10px] t-faint font-mono ml-1">
                        ({r.caseCode})
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs t-secondary">
                    {RecurringInvoicePeriodicityLabel[r.periodicity]} (día {r.dayOfMonth})
                  </td>
                  <td className="px-3 py-2 text-xs t-secondary font-mono">
                    {new Date(r.nextRunDate).toLocaleDateString('es-ES')}
                  </td>
                  <td className="px-3 py-2 text-right t-primary font-mono">
                    {formatCents(total)}
                  </td>
                  <td className="px-3 py-2 text-center text-xs t-muted">{r.generatedCount}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STATUS_STYLES[r.status]}`}>
                      {RecurringInvoiceStatusLabel[r.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => void action(r.id, 'generate-now')}
                        disabled={r.status !== 'ACTIVE'}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-emerald-300 disabled:opacity-30"
                        title="Generar borrador ahora"
                      >
                        <Send size={12} />
                      </button>
                      {r.status === 'ACTIVE' ? (
                        <button
                          type="button"
                          onClick={() => void action(r.id, 'pause')}
                          className="w-7 h-7 grid place-items-center rounded hover:bg-amber-500/10 t-muted hover:text-amber-300"
                          title="Pausar"
                        >
                          <Pause size={12} />
                        </button>
                      ) : r.status === 'PAUSED' ? (
                        <button
                          type="button"
                          onClick={() => void action(r.id, 'resume')}
                          className="w-7 h-7 grid place-items-center rounded hover:bg-emerald-500/10 t-muted hover:text-emerald-300"
                          title="Reanudar"
                        >
                          <Play size={12} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setEditing(r)}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-toga-300"
                        title="Editar"
                      >
                        <Edit2 size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void onDelete(r)}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                        title="Eliminar"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <RecurringFormModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await load()
          }}
        />
      )}
    </div>
  )
}

// ---------- Modal alta/edición ----------

interface UiLine extends RecurringInvoiceLineInput {
  _key: string
  unitPriceInput: string
}

function RecurringFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: RecurringInvoiceDto | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const isEdit = initial !== null

  const [name, setName] = useState(initial?.name ?? '')
  const [clientId, setClientId] = useState(initial?.clientId ?? '')
  const [seriesId, setSeriesId] = useState(initial?.seriesId ?? '')
  const [periodicity, setPeriodicity] = useState<RecurringInvoicePeriodicityT>(
    initial?.periodicity ?? 'MONTHLY'
  )
  const [dayOfMonth, setDayOfMonth] = useState(String(initial?.dayOfMonth ?? 1))
  const [irpfRate, setIrpfRate] = useState(initial?.irpfRate ?? '0')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [startDate, setStartDate] = useState(
    initial?.nextRunDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10)
  )
  const [lines, setLines] = useState<UiLine[]>(() =>
    initial && initial.lines.length > 0
      ? initial.lines.map((l) => ({
          ...l,
          _key: l.id,
          unitPriceInput: centsToEurosInput(l.unitPriceCents),
        }))
      : [
          {
            _key: 'init',
            position: 0,
            description: '',
            quantity: '1',
            unitPriceCents: 0,
            unitPriceInput: '',
            discountPct: '0',
            vatRate: '0.21',
            isSupplied: false,
          },
        ]
  )

  const [clients, setClients] = useState<ClientListResponse['items']>([])
  const [series, setSeries] = useState<InvoiceSeriesDto[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.get<ClientListResponse>('/clients?pageSize=200').then((r) => setClients(r.items))
    api.get<{ items: InvoiceSeriesDto[] }>('/invoice-series').then((r) => setSeries(r.items))
  }, [])

  const totalCents = useMemo(
    () =>
      lines.reduce(
        (acc, l) => acc + Math.round((Number(l.quantity) || 0) * l.unitPriceCents),
        0
      ),
    [lines]
  )

  const updateLine = (idx: number, patch: Partial<UiLine>) => {
    setLines((prev) => {
      const copy = [...prev]
      copy[idx] = { ...copy[idx]!, ...patch }
      return copy
    })
  }

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      {
        _key: Math.random().toString(36).slice(2),
        position: prev.length,
        description: '',
        quantity: '1',
        unitPriceCents: 0,
        unitPriceInput: '',
        discountPct: '0',
        vatRate: '0.21',
        isSupplied: false,
      },
    ])
  }

  const removeLine = (idx: number) =>
    setLines((prev) => prev.filter((_, i) => i !== idx))

  const [pickerOpen, setPickerOpen] = useState(false)
  const addLineFromRate = (it: RatePickerItem) => {
    setLines((prev) => [
      ...prev,
      {
        _key: Math.random().toString(36).slice(2),
        position: prev.length,
        description: it.name,
        quantity: '1',
        unitPriceCents: it.appliedAmountCents,
        unitPriceInput: centsToEurosInput(it.appliedAmountCents),
        discountPct: '0',
        vatRate: it.vatRate.toString(),
        isSupplied: false,
      },
    ])
    // Si es iguala mensual, forzamos periodicity MENSUAL.
    if (it.kind === 'IGUALA_MENSUAL') setPeriodicity('MONTHLY')
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !clientId) {
      setError('Indica nombre y cliente')
      return
    }
    if (lines.length === 0 || lines.some((l) => !l.description.trim())) {
      setError('Todas las líneas deben tener descripción')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const payload: CreateRecurringInvoiceInput = {
        clientId,
        seriesId: seriesId || null,
        name: name.trim(),
        notes: notes.trim() || null,
        periodicity,
        dayOfMonth: Number(dayOfMonth) || 1,
        irpfRate,
        startDate: startDate || null,
        lines: lines.map((l, idx) => ({
          position: idx,
          description: l.description.trim(),
          quantity: l.quantity || '1',
          unitPriceCents: l.unitPriceCents,
          discountPct: l.discountPct,
          vatRate: l.vatRate,
          isSupplied: l.isSupplied,
        })),
      }
      if (isEdit && initial) {
        await api.patch(`/recurring-invoices/${initial.id}`, payload)
      } else {
        await api.post('/recurring-invoices', payload)
      }
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Editar iguala' : 'Nueva iguala mensual'} maxWidth="max-w-3xl">
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Nombre interno</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Iguala mensual Juan García"
              className={input}
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Cliente</span>
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className={input}
              required
            >
              <option value="">—</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Periodicidad</span>
            <select
              value={periodicity}
              onChange={(e) => setPeriodicity(e.target.value as RecurringInvoicePeriodicityT)}
              className={input}
            >
              {Object.entries(RecurringInvoicePeriodicityLabel).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs t-muted">Día del mes (1-28)</span>
            <input
              type="number"
              min={1}
              max={28}
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(e.target.value)}
              className={input}
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">Próxima generación</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={input}
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Serie de facturación</span>
            <select
              value={seriesId}
              onChange={(e) => setSeriesId(e.target.value)}
              className={input}
            >
              <option value="">(por defecto)</option>
              {series.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.prefix} · {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs t-muted">IRPF (0-1)</span>
            <select
              value={irpfRate}
              onChange={(e) => setIrpfRate(e.target.value)}
              className={input}
            >
              <option value="0">0%</option>
              <option value="0.07">7%</option>
              <option value="0.15">15%</option>
              <option value="0.19">19%</option>
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-xs t-muted">Notas (se copian a cada factura)</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className={input}
          />
        </label>

        {/* Líneas */}
        <div className="border-t border-base pt-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold t-primary">Líneas que se facturarán cada periodo</h4>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                disabled={!clientId}
                title={
                  clientId
                    ? 'Insertar desde una tarifa de iguala mensual del catálogo'
                    : 'Selecciona cliente primero'
                }
                className="px-2 py-0.5 rounded text-xs t-secondary border border-base hover:bg-hover hover:text-toga-200 disabled:opacity-50 flex items-center gap-1"
              >
                <Coins size={11} /> Desde tarifa
              </button>
              <button
                type="button"
                onClick={addLine}
                className="px-2 py-0.5 rounded text-xs t-secondary border border-base hover:bg-hover"
              >
                + Añadir línea
              </button>
            </div>
          </div>
          <RatePickerModal
            open={pickerOpen}
            clientId={clientId || null}
            allowedKinds={['IGUALA_MENSUAL']}
            onClose={() => setPickerOpen(false)}
            onPick={addLineFromRate}
          />
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider t-faint">
              <tr>
                <th className="px-1 text-left">Descripción</th>
                <th className="px-1 text-right w-14">Cant.</th>
                <th className="px-1 text-right w-24">Precio</th>
                <th className="px-1 text-right w-14">IVA</th>
                <th className="w-6"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => (
                <tr key={l._key} className="border-t border-base/40">
                  <td className="px-1 py-1">
                    <input
                      value={l.description}
                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                      placeholder="Honorarios de iguala mensual..."
                      className={`${input} text-xs`}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      value={l.quantity}
                      onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                      className={`${input} text-xs text-right`}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      value={l.unitPriceInput}
                      onChange={(e) =>
                        updateLine(idx, {
                          unitPriceInput: e.target.value,
                          unitPriceCents: parseEurosToCents(e.target.value),
                        })
                      }
                      placeholder="0,00"
                      className={`${input} text-xs text-right font-mono`}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <select
                      value={l.vatRate}
                      onChange={(e) => updateLine(idx, { vatRate: e.target.value })}
                      className={`${input} text-xs text-right`}
                    >
                      <option value="0.21">21%</option>
                      <option value="0.10">10%</option>
                      <option value="0.04">4%</option>
                      <option value="0">0%</option>
                    </select>
                  </td>
                  <td className="px-1 py-1 text-right">
                    <button
                      type="button"
                      onClick={() => removeLine(idx)}
                      className="t-muted hover:text-rose-300"
                      disabled={lines.length === 1}
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-2 text-right text-sm">
            <span className="t-faint">Subtotal por periodo:</span>{' '}
            <span className="t-primary font-mono font-semibold">{formatCents(totalCents)}</span>
          </div>
        </div>

        {error && <p className="text-xs text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Guardar' : 'Crear'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

void FileText // suprime unused warning
